import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import ColorControls from "../game/ColorControls";
import { apiUrl, areAllElementsSame, devMode } from "../../functions";
import Board from "../game/Board";
import GameOver from "../game/GameOver";
import ControlBar from "../game/ControlBar"
import { Color, GameSettings, Grid, Mode, Page, Score } from "../types/Types";

const colors: Color[] = ['a', 'b', 'c', 'd', 'e', 'f']
const gameSettings: GameSettings = {
  'daily':
    {'dimensions': [18, 18], 'moves': 0},
  'small':
    {'dimensions': [8, 8], 'moves': 20},
  'medium':
    {'dimensions': [14, 14], 'moves': 30},
  'large':
    {'dimensions': [20, 20], 'moves': 40},
}

const generateGrid = (mode: Mode): Grid => {
  const [gridWidth, gridHeight]: number[] = gameSettings[mode]['dimensions'];
  const newGrid: Grid = Array.from({ length: gridHeight }, () =>
    Array.from({ length: gridWidth }, () => colors[Math.floor(Math.random() * colors.length)])
  );

  if ( devMode() ){
    console.log(
      JSON.stringify(newGrid)
        .replaceAll('"', '')
        .replaceAll('a', '1')
        .replaceAll('b', '2')
        .replaceAll('c', '3')
        .replaceAll('d', '4')
        .replaceAll('e', '5')
        .replaceAll('f', '6')
    );
  }
  
  return newGrid;
}

const newGame = (
  mode: Mode,
  setLoading: (loading: boolean) => void,
  setGrid: (grid: Grid) => void,
  setGridOrig: (gridOrig: Grid) => void,
  setRemainingMoves: (remainingMoves: number) => void
) => {
  const newGrid = generateGrid(mode);
  setGrid(newGrid);
  
  setRemainingMoves(gameSettings[mode]['moves']);
  if (mode === 'daily') {
    setLoading(true);
    fetch(apiUrl() + '/api/get-static-daily')
      .then(response => response.json())
      .then(data => {
        const newGrid = data.grid;
        setGrid(JSON.parse(newGrid));
        setGridOrig(JSON.parse(newGrid));
        setLoading(false)
      });
  } else {
    setGridOrig(newGrid);
  }
}

const nextLevel = (
  mode: Mode,
  setGridOrig: Dispatch<SetStateAction<Grid>>,
  setGrid: Dispatch<SetStateAction<Grid>>,
  setRemainingMoves: Dispatch<SetStateAction<number>>,
  setMove: Dispatch<SetStateAction<number>>,
  reset: boolean
) => {
  const newGrid = generateGrid(mode);
  setGridOrig(newGrid);
  setGrid(newGrid);
  if ( reset )
    setRemainingMoves(gameSettings[mode]['moves']);
  else
    setRemainingMoves(m => m - 1)
  setMove(0)
}

interface GameProps {
  mode: Mode;
  setCurrentPage: Dispatch<SetStateAction<Page>>;
}

const Game: React.FC<GameProps> = ({mode, setCurrentPage}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [gridOrig, setGridOrig] = useState<Grid>([[]]);
  const [grid, setGrid] = useState<Grid>([[]]);
  const [remainingMoves, setRemainingMoves] = useState<number>(0)
  const [move, setMove] = useState<number>(0)
  const [scoreSubmitted, setScoreSubmitted] = useState<boolean>(false);
  const [scores, setScores] = useState<Score[]>([])

  useEffect(() => {
    newGame(mode, setLoading, setGrid, setGridOrig, setRemainingMoves)
  }, [mode])

  const handleColorClick = (color: string): void => {
    if (gameStopped) {
      return
    }
    setMove(move=>move+1)
    const topLeftColor: string = grid[0][0];
    const newGrid: string[][] = [];
    const visited: boolean[][] = Array.from({ length: grid.length }, () => Array.from({ length: grid[0].length }, () => false));

    const changeColor = (row: number, col: number): void => {
      if (row < 0 || row >= grid.length || col < 0 || col >= grid[0].length || visited[row][col]) {
        return;
      }

      visited[row][col] = true

      if (grid[row][col] === topLeftColor) {
        newGrid[row][col] = color;
        changeColor(row + 1, col); // Down
        changeColor(row - 1, col); // Up
        changeColor(row, col + 1); // Right
        changeColor(row, col - 1); // Left
      }
    };

    for (let row = 0; row < grid.length; row++) {
      newGrid[row] = [...grid[row]];
    }

    changeColor(0, 0);
    setGrid(newGrid);    
  };

  const updateScore = useCallback(() => {
    fetch(apiUrl() + '/api/get-static-scores')
      .then(response => response.json())
      .then((data) => data.sort((a: { score: number }, b: { score: number }) => a.score - b.score))
      .then((data) => setScores(data))
  }, []);

  const showNextLevel = mode !== 'daily' && areAllElementsSame(grid)
  const showOutOfMoves = mode !== 'daily' && remainingMoves - move <= 0 && ! areAllElementsSame(grid)
  const showGameOver = mode === 'daily' && areAllElementsSame(grid)
  const gameStopped = showNextLevel || showOutOfMoves || showGameOver
  const [currentColor, setCurrentColor] = useState<null|string>(null);

  return (
    <div className="flex flex-col items-center">
      <ControlBar
        gridOrig={gridOrig} move={move} setCurrentPage={setCurrentPage} 
        setMove={setMove} setGrid={setGrid} gameStopped={gameStopped} />

      <Board grid={grid} move={move} mode={mode} loading={loading} 
        scores={scores} remainingMoves={remainingMoves}
        updateScore={updateScore} nextLevel={nextLevel} setMove={setMove}
        setGridOrig={setGridOrig} setGrid={setGrid} setRemainingMoves={setRemainingMoves}
        showNextLevel={showNextLevel} showOutOfMoves={showOutOfMoves} setCurrentPage={setCurrentPage}
        modeMoves={gameSettings[mode]['moves']}
      />

      <ColorControls colors={colors} handleColorClick={handleColorClick} setCurrentColor={setCurrentColor} currentColor={currentColor}/>

      {showGameOver && (
        <GameOver grid={grid} mode={mode} updateScore={updateScore} scoreSubmitted={scoreSubmitted}
          gridOrig={gridOrig} setMove={setMove} setGrid={setGrid} remainingMoves={remainingMoves}
          setCurrentPage={setCurrentPage} setScoreSubmitted={setScoreSubmitted} move={move}   />
      )}
    </div>
  );
}
 
export default Game;
